import {
	Card,
	CardBody,
	CardHeader,
	CardHeaderActions,
	CardHeading,
	DEFAULT_DATE_FORMAT,
	DetailsPageInfoField,
	DetailsPageInfoFields,
	FormattedDateTime,
	Id,
	SelectPopover,
	useEditDrawer,
} from "@dgs/core";
import { autoPlacement, shift } from "@floating-ui/react";
import { isSameDay } from "date-fns";
import { FC, ReactNode, useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { EventRating } from "../events/EventRating";
import { EventCalendarResource } from "./events/calendarEventService";

export const useEventPopover = () => {
	const { handleOpen: openPopover, editEntityId: eventId } = useEditDrawer();
	const [popOverAnchor, setPopOverAnchor] = useState<HTMLElement | null>(null);

	return {
		openPopover: (e: React.MouseEvent<HTMLElement>, id: Id) => {
			openPopover(id);
			setPopOverAnchor(e.currentTarget);
		},
		popoverProps: {
			closePopover: () => setPopOverAnchor(null),
			popOverAnchor,
			eventId,
		},
		eventId,
	};
};
const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

interface Props {
	event: EventCalendarResource | null;
	closePopover: () => void;
	popOverAnchor: HTMLElement | null;
	renderActions: (event: EventCalendarResource) => ReactNode;
}
export const EventPopover: FC<Props> = ({ event, closePopover, popOverAnchor, renderActions }) => {
	const { t } = useTranslation();

	if (!popOverAnchor || !event) return;
	return (
		<SelectPopover
			anchor={popOverAnchor}
			middlewares={[autoPlacement({ allowedPlacements: ["right", "left", "top", "bottom"] }), shift()]}
			onClose={closePopover}
		>
			<Card>
				<StyledCardHeader>
					<CardHeading>{event.name}</CardHeading>
					<CardHeaderActions>
						{event.eventType.eventGoalRatingMetricRequirement !== "prohibited" && !!event.rating && (
							<EventRating rating={event.rating} />
						)}
						{renderActions(event)}
					</CardHeaderActions>
				</StyledCardHeader>
				<StyledCardBody>
					<DetailsPageInfoFields>
						<DetailsPageInfoField
							title={t("Date")}
							value={
								<>
									<FormattedDateTime dateTime={event.start} format={DEFAULT_DATE_FORMAT} />
									{!isSameDay(event.start, event.end) && (
										<>
											{" - "} <FormattedDateTime dateTime={event.end} format={DEFAULT_DATE_FORMAT} />
										</>
									)}
								</>
							}
						/>
						{event.timezone !== userTimezone ? (
							<DetailsPageInfoField
								title={t("Datum in Zeitzone des Events")}
								value={
									<>
										<div>
											<FormattedDateTime
												dateTime={event.start}
												format={{ ...DEFAULT_DATE_FORMAT, timeZone: event.timezone }}
											/>
											{!isSameDay(event.start, event.end) && (
												<>
													{" - "}{" "}
													<FormattedDateTime
														dateTime={event.end}
														format={{ ...DEFAULT_DATE_FORMAT, timeZone: event.timezone }}
													/>
												</>
											)}
										</div>
										{event.timezone && <div>({event.timezone})</div>}
									</>
								}
							/>
						) : (
							<DetailsPageInfoField title={t("Timezone")} value={event.timezone} />
						)}
						<DetailsPageInfoField title={t("Event type")} value={event.eventType.name} />
						{!!event.location && <DetailsPageInfoField title={t("Location")} value={event.location.name} />}
					</DetailsPageInfoFields>
				</StyledCardBody>
			</Card>
		</SelectPopover>
	);
};

const StyledCardHeader = styled(CardHeader)`
	gap: ${({ theme }) => theme.spacing(4)};
`;

const StyledCardBody = styled(CardBody)`
	display: grid;
	gap: ${({ theme }) => theme.spacing(2)};
`;
